import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function LoginForm({ setError }) {
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setNeedsVerification(false);
    setResendMessage("");
    const result = await login(form.email, form.password);
    if (!result?.ok) {
      if (result?.code === "EMAIL_NOT_VERIFIED") {
        setNeedsVerification(true);
        const baseError = result?.error || "Please verify your email before signing in.";
        setError(baseError);
        if (form.email) {
          setResending(true);
          const resendResult = await resendVerification(form.email);
          if (resendResult?.ok) {
            setResendMessage(
              resendResult?.message || "We sent a new verification email. Please check your inbox."
            );
          } else if (resendResult?.error) {
            setError(`${baseError} ${resendResult.error}`);
          }
          setResending(false);
        }
        setLoading(false);
        return;
      }
      setError(result?.error || "Login failed");
      setLoading(false);
      return;
    }
    try {
      navigate("/chat");
    } catch (error) {
      setError(error?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (!form.email) {
      setError("Please enter your email first");
      return;
    }
    setResending(true);
    setError("");
    setResendMessage("");
    const result = await resendVerification(form.email);
    if (!result?.ok) {
      setError(result?.error || "Could not resend verification email");
      setResending(false);
      return;
    }
    setResendMessage(result?.message || "Verification email sent. Please check your inbox.");
    setResending(false);
  };

  return (
    <form onSubmit={onSubmit} className="auth-form">
      <label>Email<input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required /></label>
      <label>
        Password
        <div className="password-field">
          <input
            type={showPassword ? "text" : "password"}
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            minLength={6}
            required
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            title={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C7 20 2.73 16.11 1 12c.72-1.71 1.81-3.23 3.17-4.46" />
                <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c5 0 9.27 3.89 11 8a10.95 10.95 0 0 1-1.67 2.68" />
                <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                <path d="m1 1 22 22" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
      </label>
      <button type="submit" className="primary-btn" disabled={loading}>{loading ? "Signing in..." : "Sign In"}</button>
      {needsVerification ? (
        <button type="button" className="ghost-btn" onClick={onResend} disabled={resending}>
          {resending ? "Sending email..." : "Resend verification email"}
        </button>
      ) : null}
      {resendMessage ? <p className="auth-helper">{resendMessage}</p> : null}
      <p className="auth-helper">
        Don&apos;t have an account?{" "}
        <button type="button" className="auth-link-btn" onClick={() => navigate("/auth/register")}>
          Sign Up
        </button>
      </p>
    </form>
  );
}
